import apiClient from './client';
import { API_ENDPOINTS } from './config';
import type { Provider, PaginationInfo } from '../stores/providersStore';

// Request types
export interface CreateProviderRequest {
	name: string;
	email: string;
	phone: string;
	address: string;
	city: string;
	state: string;
	zipCode: string;
	joinedDate: string;
	baseBbpFee: number;
	bookingEnabled: boolean;
	notes?: string;
	businessName: string;
}

export interface ListProvidersParams {
	page?: number;
	size?: number;
	sort?: string;
	business?: string;
}

// Update request type (same fields as create, but for PUT)
export interface UpdateProviderRequest {
	name: string;
	email: string;
	phone: string;
	address: string;
	city: string;
	state: string;
	zipCode: string;
	joinedDate: string;
	baseBbpFee: number;
	bookingEnabled: boolean;
	notes?: string;
	businessName: string;
}

// List response matches the API schema provided
export interface ListProvidersResponse {
	content: Provider[];
	page: number;
	size: number;
	totalElements: number;
	totalPages: number;
	first: boolean;
	last: boolean;
}

// Location in provider detail response
export interface ProviderLocation {
	id: number;
	providerUserId: number;
	providerId: number;
	locationName: string;
	address: string;
	city: string;
	state: string;
	zipCode: string;
	country: string;
	phone: string;
	active: boolean;
	primary: boolean;
	notes?: string;
	createdAt: string;
	updatedAt: string;
}

// Appointment status enum
export type AppointmentStatus = 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED';

// Recent appointment in provider detail response
export interface ProviderAppointment {
	id: number;
	providerId: number;
	providerName: string;
	customerId: number;
	customerName: string;
	customerPhone: string;
	staffUserId: number;
	appointmentDateTime: string;
	amount: number;
	status: AppointmentStatus;
	serviceDescription: string;
	durationMinutes: number;
	createdAt: string;
	updatedAt: string;
}

// Detail response - address fields removed, now in locations array
export interface ProviderDetailResponse {
	id: number;
	name: string;
	totalAppointments: number;
	totalRevenue: number;
	completedAppointments: number;
	email: string;
	businessName: string;
	phone: string;
	joinedDate: string;
	baseBbpFee: number;
	bbpFee: number;
	bookingEnabled: boolean;
	notes?: string;
	accountStatus: boolean;
	averageRating: number;
	totalRatings: number;
	createdAt: string;
	updatedAt: string;
	locations: ProviderLocation[];
	recentAppointments: ProviderAppointment[];
}

// Helper to get primary location from locations array
export const getPrimaryLocation = (
	locations: ProviderLocation[] | undefined
): ProviderLocation | null => {
	if (!locations || locations.length === 0) return null;
	return locations.find(loc => loc.primary) || locations[0];
};

// Provider API methods
export const providersApi = {
	// Create a new provider (returns void - no response body)
	create: async (data: CreateProviderRequest): Promise<void> => {
		await apiClient.post<void>(API_ENDPOINTS.PROVIDERS.CREATE, data);
	},

	// Update an existing provider (returns full provider)
	update: async (id: number, data: UpdateProviderRequest): Promise<ProviderDetailResponse> => {
		return apiClient.put<ProviderDetailResponse>(API_ENDPOINTS.PROVIDERS.UPDATE(id), data);
	},

	// List providers with pagination
	list: async (params: ListProvidersParams = {}): Promise<ListProvidersResponse> => {
		const { page = 0, size = 20, sort = 'name,asc' } = params;

		const queryParams = new URLSearchParams({
			page: page.toString(),
			size: size.toString(),
			sort,
		});

		return apiClient.get<ListProvidersResponse>(
			`${API_ENDPOINTS.PROVIDERS.LIST}?${queryParams.toString()}`
		);
	},

	// Get single provider by ID
	getById: async (id: number): Promise<ProviderDetailResponse> => {
		return apiClient.get<ProviderDetailResponse>(API_ENDPOINTS.PROVIDERS.DETAIL(id));
	},
};

// Helper to extract pagination info from response
export const extractPaginationInfo = (response: ListProvidersResponse): PaginationInfo => ({
	page: response.page,
	size: response.size,
	totalElements: response.totalElements,
	totalPages: response.totalPages,
	first: response.first,
	last: response.last,
});
